// src/components/ErrorMessage.jsx
import React from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Collapse,
  IconButton,
  Typography,
  Paper
} from '@mui/material';
import {
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
  Refresh as RefreshIcon,
  BugReport as BugReportIcon,
  Help as HelpIcon
} from '@mui/icons-material';

/**
 * Enhanced Error Message Component
 * Displays contextual error messages with suggested actions
 *
 * @param {string} error - Error message or object
 * @param {string} title - Error title (optional)
 * @param {string} severity - Alert severity (error, warning, info)
 * @param {string} context - Context of where error occurred
 * @param {Function} onRetry - Retry action callback
 * @param {Function} onClose - Close alert callback
 * @param {boolean} showDetails - Show technical details
 * @param {Array} suggestions - Array of suggestion objects {text, action, icon}
 */
const ErrorMessage = ({
  error,
  title,
  severity = 'error',
  context,
  onRetry,
  onClose,
  showDetails = false,
  suggestions = []
}) => {
  const [expanded, setExpanded] = React.useState(false);
  const [closed, setClosed] = React.useState(false);

  if (closed) return null;

  // Parse error message
  const getErrorMessage = () => {
    if (typeof error === 'string') return error;
    if (error?.response?.data?.message) return error.response.data.message;
    if (error?.message) return error.message;
    return 'An unexpected error occurred';
  };

  const getErrorDetails = () => {
    if (typeof error === 'object') {
      return {
        status: error?.response?.status,
        statusText: error?.response?.statusText,
        endpoint: error?.config?.url,
        method: error?.config?.method?.toUpperCase(),
        stack: error?.stack
      };
    }
    return null;
  };

  // Generate contextual suggestions
  const getContextualSuggestions = () => {
    const errorMsg = getErrorMessage().toLowerCase();
    const details = getErrorDetails();
    const contextualSuggestions = [];

    // Network errors
    if (errorMsg.includes('network') || errorMsg.includes('timeout')) {
      contextualSuggestions.push({
        text: 'Check your internet connection and try again',
        icon: <RefreshIcon />
      });
    }

    // Authentication errors
    if (details?.status === 401 || errorMsg.includes('unauthorized') || errorMsg.includes('authentication')) {
      contextualSuggestions.push({
        text: 'Your session may have expired. Try logging in again',
        action: () => window.location.href = '/login',
        actionLabel: 'Go to Login',
        icon: <RefreshIcon />
      });
    }

    // Permission errors
    if (details?.status === 403 || errorMsg.includes('forbidden') || errorMsg.includes('permission')) {
      contextualSuggestions.push({
        text: 'You don\'t have permission to perform this action',
        icon: <HelpIcon />
      });
      contextualSuggestions.push({
        text: 'Contact your administrator to request access',
        icon: <HelpIcon />
      });
    }

    // Not found errors
    if (details?.status === 404 || errorMsg.includes('not found')) {
      contextualSuggestions.push({
        text: 'The requested resource was not found',
        icon: <HelpIcon />
      });
      if (context === 'tickets') {
        contextualSuggestions.push({
          text: 'The ticket may have been deleted or moved',
          icon: <HelpIcon />
        });
      }
    }

    // Server errors
    if (details?.status >= 500 || errorMsg.includes('server error')) {
      contextualSuggestions.push({
        text: 'The server encountered an error. Please try again later',
        icon: <RefreshIcon />
      });
      contextualSuggestions.push({
        text: 'If the problem persists, contact support',
        action: () => window.location.href = '/tickets/new',
        actionLabel: 'Create Support Ticket',
        icon: <BugReportIcon />
      });
    }

    // Validation errors
    if (details?.status === 400 || errorMsg.includes('invalid') || errorMsg.includes('validation')) {
      contextualSuggestions.push({
        text: 'Check that all required fields are filled correctly',
        icon: <HelpIcon />
      });
    }

    // File upload errors
    if (context === 'upload' || errorMsg.includes('upload')) {
      contextualSuggestions.push({
        text: 'Check file size and format requirements',
        icon: <HelpIcon />
      });
      contextualSuggestions.push({
        text: 'Ensure the file is not corrupted',
        icon: <HelpIcon />
      });
    }

    return [...contextualSuggestions, ...suggestions];
  };

  const errorMessage = getErrorMessage();
  const errorDetails = getErrorDetails();
  const allSuggestions = getContextualSuggestions();

  const handleClose = () => {
    setClosed(true);
    if (onClose) onClose();
  };

  return (
    <Alert
      severity={severity}
      sx={{ mb: 2 }}
      action={
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {onRetry && (
            <IconButton
              size="small"
              color="inherit"
              onClick={onRetry}
              title="Retry"
            >
              <RefreshIcon fontSize="small" />
            </IconButton>
          )}
          {showDetails && errorDetails && (
            <IconButton
              size="small"
              color="inherit"
              onClick={() => setExpanded(!expanded)}
              title="Show details"
              sx={{
                transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.3s'
              }}
            >
              <ExpandMoreIcon fontSize="small" />
            </IconButton>
          )}
          <IconButton
            size="small"
            color="inherit"
            onClick={handleClose}
            title="Dismiss"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      }
    >
      <AlertTitle>{title || 'Error'}</AlertTitle>
      <Typography variant="body2" sx={{ mb: allSuggestions.length > 0 ? 2 : 0 }}>
        {errorMessage}
      </Typography>

      {/* Contextual suggestions */}
      {allSuggestions.length > 0 && (
        <Box sx={{ mt: 1 }}>
          <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 1 }}>
            Suggestions:
          </Typography>
          {allSuggestions.map((suggestion, idx) => (
            <Box key={idx} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 0.5 }}>
              <Box sx={{ mt: 0.25 }}>{suggestion.icon}</Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant="caption" sx={{ display: 'block' }}>
                  • {suggestion.text}
                </Typography>
                {suggestion.action && (
                  <Button
                    size="small"
                    variant="text"
                    onClick={suggestion.action}
                    sx={{ mt: 0.5, p: 0, minWidth: 'auto' }}
                  >
                    {suggestion.actionLabel || 'Take Action'}
                  </Button>
                )}
              </Box>
            </Box>
          ))}
        </Box>
      )}

      {/* Technical details (collapsible) */}
      {showDetails && errorDetails && (
        <Collapse in={expanded}>
          <Paper
            sx={{
              mt: 2,
              p: 1.5,
              bgcolor: 'rgba(0, 0, 0, 0.1)',
              fontFamily: 'monospace',
              fontSize: '0.75rem'
            }}
          >
            <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 1 }}>
              Technical Details:
            </Typography>
            {errorDetails.status && (
              <Typography variant="caption" display="block">
                Status: {errorDetails.status} {errorDetails.statusText}
              </Typography>
            )}
            {errorDetails.method && errorDetails.endpoint && (
              <Typography variant="caption" display="block">
                Request: {errorDetails.method} {errorDetails.endpoint}
              </Typography>
            )}
            {errorDetails.stack && process.env.NODE_ENV === 'development' && (
              <Box sx={{ mt: 1, maxHeight: 200, overflow: 'auto' }}>
                <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
                  Stack Trace:
                </Typography>
                <Typography
                  variant="caption"
                  component="pre"
                  sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}
                >
                  {errorDetails.stack}
                </Typography>
              </Box>
            )}
          </Paper>
        </Collapse>
      )}
    </Alert>
  );
};

export default ErrorMessage;
